"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations, useLocale } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const STEP_KEYS = [
    "GoLive_world_step1",
    "GoLive_world_step2",
    "GoLive_world_step3",
];

// Globe : centre + rayon (repère du viewBox 0 0 360 220).
const G = { cx: 180, cy: 110, r: 84 };
const CITIES = [
    { key: "GoLive_world_city_paris", x: 150, y: 70 },
    { key: "GoLive_world_city_montreal", x: 120, y: 104 },
    { key: "GoLive_world_city_tokyo", x: 240, y: 92 },
    { key: "GoLive_world_city_saopaulo", x: 150, y: 158 },
    { key: "GoLive_world_city_sydney", x: 232, y: 150 },
];
const ARCS = [
    [0, 2],
    [1, 3],
    [2, 4],
    [0, 1],
];
const VISITOR_TARGET = 1248;

function dirOf(p) {
    const vx = p.x - G.cx;
    const vy = p.y - G.cy;
    const len = Math.hypot(vx, vy) || 1;
    return { nx: vx / len, ny: vy / len };
}

function arcPath(a, b) {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    let vx = mx - G.cx;
    let vy = my - G.cy;
    const len = Math.hypot(vx, vy) || 1;
    vx /= len;
    vy /= len;
    const qx = mx + vx * 26;
    const qy = my + vy * 26;
    return `M ${a.x} ${a.y} Q ${qx.toFixed(1)} ${qy.toFixed(1)} ${b.x} ${b.y}`;
}

export default function GoLiveWorld() {
    const t = useTranslations();
    const locale = useLocale();
    const reduce = useReducedMotion();

    const [phase, setPhase] = useState("local"); // local | deploying | live
    const [doneSteps, setDoneSteps] = useState(0);
    const [visitors, setVisitors] = useState(0);
    const timers = useRef([]);

    function clearTimers() {
        timers.current.forEach((id) => clearTimeout(id));
        timers.current = [];
    }

    function start() {
        if (phase === "deploying") return;
        clearTimers();
        setPhase("deploying");
        setDoneSteps(0);
        setVisitors(0);
        const stepMs = reduce ? 220 : 680;
        for (let i = 1; i <= STEP_KEYS.length; i++) {
            timers.current.push(setTimeout(() => setDoneSteps(i), stepMs * i));
        }
        timers.current.push(
            setTimeout(
                () => setPhase("live"),
                stepMs * (STEP_KEYS.length + 1),
            ),
        );
    }

    function reset() {
        clearTimers();
        setPhase("local");
        setDoneSteps(0);
        setVisitors(0);
    }

    useEffect(() => clearTimers, []);

    // Compteur de visiteurs : requestAnimationFrame (jamais une MotionValue en enfant JSX).
    useEffect(() => {
        if (phase !== "live") return undefined;
        if (reduce) {
            setVisitors(VISITOR_TARGET);
            return undefined;
        }
        let raf;
        let startTs = null;
        const dur = 1500;
        const tick = (ts) => {
            if (startTs === null) startTs = ts;
            const p = Math.min(1, (ts - startTs) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setVisitors(Math.round(eased * VISITOR_TARGET));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [phase, reduce]);

    const isLive = phase === "live";
    const isDeploying = phase === "deploying";

    return (
        <section id="golive-world" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("GoLive_world_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("GoLive_world_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="planet-bold"
                            className="w-3.5 h-3.5 text-cyan-300"
                        />
                        mon-site
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("GoLive_world_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                    {/* Cadre navigateur */}
                    <div className="min-w-0">
                        <div
                            className={`rounded-2xl overflow-hidden bg-white shadow-2xl shadow-black/40 transition-all duration-300 ${
                                isLive
                                    ? "ring-2 ring-emerald-400/50"
                                    : "ring-1 ring-black/10"
                            }`}
                        >
                            {/* Barre d'adresse */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                                <div className="flex gap-1.5 shrink-0">
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                </div>
                                <div className="flex-1 ml-1 inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-200 min-w-0">
                                    {isLive ? (
                                        <>
                                            <SolarIcon
                                                name="lock-keyhole-minimalistic-bold"
                                                className="w-3 h-3 text-emerald-500 shrink-0"
                                            />
                                            <span className="text-[11px] font-medium text-slate-600 truncate">
                                                https://mon-site.fr
                                            </span>
                                        </>
                                    ) : isDeploying ? (
                                        <>
                                            <SolarIcon
                                                name="refresh-bold"
                                                className="w-3 h-3 text-cyan-500 shrink-0 animate-spin"
                                            />
                                            <span className="text-[11px] font-medium text-slate-400 truncate">
                                                {t("GoLive_world_addr_deploying")}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <SolarIcon
                                                name="monitor-bold"
                                                className="w-3 h-3 text-slate-400 shrink-0"
                                            />
                                            <span className="text-[11px] font-medium text-slate-400 truncate">
                                                localhost:3000
                                            </span>
                                        </>
                                    )}
                                </div>
                                <span
                                    className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        isLive
                                            ? "bg-emerald-100 text-emerald-700"
                                            : "bg-slate-200 text-slate-500"
                                    }`}
                                >
                                    {isLive
                                        ? t("GoLive_world_chip_live")
                                        : t("GoLive_world_chip_private")}
                                </span>
                            </div>

                            {/* Aperçu du site */}
                            <div
                                className={`p-5 sm:p-6 min-h-[208px] transition-all duration-500 ${
                                    isLive ? "" : "opacity-60 grayscale"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 rounded-lg bg-cyan-500" />
                                        <span className="h-2.5 w-16 rounded-full bg-slate-200" />
                                    </div>
                                    <div className="flex gap-1.5">
                                        <span className="h-2 w-7 rounded-full bg-slate-200" />
                                        <span className="h-2 w-7 rounded-full bg-slate-200" />
                                        <span className="h-2 w-7 rounded-full bg-slate-200" />
                                    </div>
                                </div>
                                <div className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900">
                                    {t("GoLive_world_site_heading")}
                                </div>
                                <div className="mt-3 space-y-2">
                                    <div className="h-2.5 rounded-full bg-slate-200 w-full" />
                                    <div className="h-2.5 rounded-full bg-slate-200 w-4/5" />
                                </div>
                                <div className="mt-5 inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white bg-cyan-500">
                                    {t("GoLive_world_site_button")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Étapes + action */}
                    <div className="min-w-0 flex flex-col gap-5">
                        <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300/90 mb-3">
                                {t("GoLive_world_steps_label")}
                            </div>
                            <div className="flex flex-col gap-2.5">
                                {STEP_KEYS.map((k, i) => {
                                    const done = doneSteps > i;
                                    const current = isDeploying && doneSteps === i;
                                    return (
                                        <div
                                            key={k}
                                            className="flex items-center gap-3"
                                        >
                                            {done ? (
                                                <SolarIcon
                                                    name="check-circle-bold"
                                                    className="w-6 h-6 shrink-0 text-emerald-400"
                                                />
                                            ) : (
                                                <span
                                                    className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${
                                                        current
                                                            ? "bg-cyan-500/20 text-cyan-300"
                                                            : "bg-white/5 text-gray-500"
                                                    }`}
                                                >
                                                    {current && !reduce ? (
                                                        <SolarIcon
                                                            name="refresh-bold"
                                                            className="w-3.5 h-3.5 animate-spin"
                                                        />
                                                    ) : (
                                                        <span className="text-[11px] font-bold">
                                                            {i + 1}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            <span
                                                className={`text-sm ${
                                                    done || current
                                                        ? "text-white"
                                                        : "text-gray-400"
                                                }`}
                                            >
                                                {t(k)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            {phase === "local" && (
                                <button
                                    type="button"
                                    onClick={start}
                                    className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 px-5 py-3 text-sm font-semibold text-bgDark-900 shadow-lg shadow-cyan-500/25 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="rocket-2-bold-duotone"
                                        className="w-5 h-5"
                                    />
                                    {t("GoLive_world_cta")}
                                </button>
                            )}
                            {isDeploying && (
                                <div className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300">
                                    <SolarIcon
                                        name="refresh-bold"
                                        className="w-4 h-4 animate-spin"
                                    />
                                    {t("GoLive_world_deploying")}
                                </div>
                            )}
                            {isLive && (
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300">
                                        <SolarIcon
                                            name="check-circle-bold"
                                            className="w-4 h-4"
                                        />
                                        {t("GoLive_world_live_label")}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={reset}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-xs text-gray-400 transition active:scale-95"
                                    >
                                        <SolarIcon
                                            name="restart-bold-duotone"
                                            className="w-3.5 h-3.5"
                                        />
                                        {t("GoLive_world_reset")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Révélation : le globe des visiteurs */}
                <AnimatePresence>
                    {isLive && (
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: reduce ? 0 : 0.4 }}
                            className="relative px-5 sm:px-7 pb-7"
                        >
                            <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-5 sm:p-6">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                                    <div className="text-sm font-semibold text-white">
                                        {t("GoLive_world_map_title")}
                                    </div>
                                    <div className="flex items-center gap-5">
                                        <div className="text-right">
                                            <div className="text-xl font-bold text-cyan-300 tabular-nums">
                                                {visitors.toLocaleString(locale)}
                                            </div>
                                            <div className="text-[11px] text-gray-400">
                                                {t("GoLive_world_visitors")}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xl font-bold text-cyan-300 tabular-nums">
                                                42
                                            </div>
                                            <div className="text-[11px] text-gray-400">
                                                {t("GoLive_world_countries")}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="w-56 h-56 bg-cyan-500/20 rounded-full blur-3xl" />
                                    </div>
                                    <svg
                                        viewBox="0 0 360 220"
                                        width="100%"
                                        className="relative max-h-[300px]"
                                        role="img"
                                        aria-hidden="true"
                                    >
                                        <defs>
                                            <radialGradient
                                                id="goliveGlobeGrad"
                                                cx="38%"
                                                cy="32%"
                                                r="78%"
                                            >
                                                <stop
                                                    offset="0%"
                                                    stopColor="#0e7490"
                                                    stopOpacity="0.55"
                                                />
                                                <stop
                                                    offset="55%"
                                                    stopColor="#0b2536"
                                                    stopOpacity="0.6"
                                                />
                                                <stop
                                                    offset="100%"
                                                    stopColor="#0b1220"
                                                    stopOpacity="0.9"
                                                />
                                            </radialGradient>
                                        </defs>

                                        {/* Sphère */}
                                        <circle
                                            cx={G.cx}
                                            cy={G.cy}
                                            r={G.r}
                                            fill="url(#goliveGlobeGrad)"
                                            stroke="#22d3ee"
                                            strokeOpacity="0.3"
                                            strokeWidth="1.5"
                                        />
                                        {/* Méridiens / parallèles */}
                                        <g
                                            fill="none"
                                            stroke="#22d3ee"
                                            strokeOpacity="0.18"
                                            strokeWidth="1"
                                        >
                                            <ellipse
                                                cx={G.cx}
                                                cy={G.cy}
                                                rx="56"
                                                ry="84"
                                            />
                                            <ellipse
                                                cx={G.cx}
                                                cy={G.cy}
                                                rx="28"
                                                ry="84"
                                            />
                                            <line
                                                x1={G.cx}
                                                y1={G.cy - G.r}
                                                x2={G.cx}
                                                y2={G.cy + G.r}
                                            />
                                            <ellipse
                                                cx={G.cx}
                                                cy={G.cy}
                                                rx="84"
                                                ry="14"
                                            />
                                            <ellipse
                                                cx={G.cx}
                                                cy={G.cy - 42}
                                                rx="72"
                                                ry="12"
                                            />
                                            <ellipse
                                                cx={G.cx}
                                                cy={G.cy + 42}
                                                rx="72"
                                                ry="12"
                                            />
                                        </g>

                                        {/* Arcs de trafic */}
                                        {ARCS.map(([ai, bi], i) => {
                                            const d = arcPath(
                                                CITIES[ai],
                                                CITIES[bi],
                                            );
                                            return reduce ? (
                                                <path
                                                    key={`arc-${i}`}
                                                    d={d}
                                                    fill="none"
                                                    stroke="#67e8f9"
                                                    strokeOpacity="0.35"
                                                    strokeWidth="1.2"
                                                />
                                            ) : (
                                                <motion.path
                                                    key={`arc-${i}`}
                                                    d={d}
                                                    fill="none"
                                                    stroke="#67e8f9"
                                                    strokeOpacity="0.45"
                                                    strokeWidth="1.3"
                                                    strokeDasharray="2 8"
                                                    initial={{
                                                        strokeDashoffset: 30,
                                                    }}
                                                    animate={{
                                                        strokeDashoffset: 0,
                                                    }}
                                                    transition={{
                                                        duration: 1.4,
                                                        repeat: Infinity,
                                                        ease: "linear",
                                                        delay: i * 0.25,
                                                    }}
                                                />
                                            );
                                        })}

                                        {/* Villes : pins + halo + libellés */}
                                        {CITIES.map((c, i) => {
                                            const { nx, ny } = dirOf(c);
                                            const lx = G.cx + nx * (G.r + 13);
                                            const ly = G.cy + ny * (G.r + 13);
                                            const anchor =
                                                nx > 0.2
                                                    ? "start"
                                                    : nx < -0.2
                                                      ? "end"
                                                      : "middle";
                                            return (
                                                <g key={c.key}>
                                                    <line
                                                        x1={c.x}
                                                        y1={c.y}
                                                        x2={G.cx + nx * (G.r + 4)}
                                                        y2={G.cy + ny * (G.r + 4)}
                                                        stroke="#22d3ee"
                                                        strokeOpacity="0.25"
                                                        strokeWidth="1"
                                                    />
                                                    {!reduce && (
                                                        <motion.circle
                                                            cx={c.x}
                                                            cy={c.y}
                                                            initial={{
                                                                r: 3,
                                                                opacity: 0.55,
                                                            }}
                                                            animate={{
                                                                r: [3, 11],
                                                                opacity: [
                                                                    0.55, 0,
                                                                ],
                                                            }}
                                                            transition={{
                                                                duration: 1.8,
                                                                repeat: Infinity,
                                                                delay: i * 0.3,
                                                                ease: "easeOut",
                                                            }}
                                                            fill="#22d3ee"
                                                        />
                                                    )}
                                                    <circle
                                                        cx={c.x}
                                                        cy={c.y}
                                                        r="3.2"
                                                        fill="#67e8f9"
                                                    />
                                                    <circle
                                                        cx={c.x}
                                                        cy={c.y}
                                                        r="1.3"
                                                        fill="#ecfeff"
                                                    />
                                                    <text
                                                        x={lx}
                                                        y={ly + 3}
                                                        fontSize="9.5"
                                                        fontFamily="ui-monospace, monospace"
                                                        textAnchor={anchor}
                                                        fill="#cbd5e1"
                                                    >
                                                        {t(c.key)}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </svg>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("GoLive_world_caption")}
            </p>
        </section>
    );
}
